/**
 * Blog Editor Page
 * A clean, writer-focused blog creation/editing experience
 * Features responsive design with appropriate text sizing and minimal UI
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RichTextEditor, Button, Input, Alert } from '@/shared/components/ui';
import { useCreateBlog } from '@/features/blog/operations/useCreateBlog';
import { useUpdateBlog } from '@/features/blog/operations/useUpdateBlog';
import { useFetchBlogs } from '@/features/blog/operations/useFetchBlogs';
import { CreateBlogRequest, UpdateBlogRequest } from '@/features/blog/api';
import { ROUTES } from '@/shared/config';
import { useSafeThemeClasses, useTheme } from '@/shared/theme';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';

interface BlogFormData {
	title: string;
	content: string;
	excerpt: string;
	category: string;
	tags: string[];
	is_published: boolean;
	featured_image_url: string;
}

interface FieldRestrictions {
	title?: boolean;
	content?: boolean;
	excerpt?: boolean;
	category?: boolean;
	tags?: boolean;
	is_published?: boolean;
	featured_image_url?: boolean;
}

const BlogEditorPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isEditMode = !!id;
	const theme = useSafeThemeClasses();
	const { resolvedTheme } = useTheme();

	// Hooks
	const { createBlog, loading: createLoading, error: createError } = useCreateBlog();
	const { updateBlog, loading: updateLoading, error: updateError } = useUpdateBlog();
	const { getBlogById, loading: fetchLoading, error: fetchError } = useFetchBlogs();

	// State
	const [formData, setFormData] = useState<BlogFormData>({
		title: '',
		content: '',
		excerpt: '',
		category: '',
		tags: [],
		is_published: false,
		featured_image_url: '',
	});

	const [tagInput, setTagInput] = useState('');
	const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

	// Field restrictions for edit mode
	const editRestrictions: FieldRestrictions = {};

	// Check if blog exists for edit mode and load data
	useEffect(() => {
		if (isEditMode && id) {
			const fetchBlogData = async () => {
				try {
					const existingBlog = await getBlogById(id);

					if (existingBlog) {
						setFormData({
							title: existingBlog.title,
							content: existingBlog.content,
							excerpt: existingBlog.excerpt || '',
							category: existingBlog.category || '',
							tags: existingBlog.tags || [],
							is_published: existingBlog.is_published || false,
							featured_image_url: existingBlog.featured_image_url || '',
						});
					} else {
						toast.error('Blog not found');
						navigate(ROUTES.DRAFTS, { replace: true });
					}
				} catch (error) {
					logger.error('Error fetching blog', 'BlogEditorPage', { error, blogId: id });
					toast.error('Failed to load blog for editing');
					navigate(ROUTES.DRAFTS, { replace: true });
				}
			};

			fetchBlogData();
		}
	}, [isEditMode, id, getBlogById, navigate]);

	// Handle form field changes
	const handleFieldChange = (field: keyof BlogFormData, value: string | string[] | boolean) => {
		if (isEditMode && editRestrictions[field]) {
			return;
		}

		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	// Handle tag addition
	const handleAddTag = () => {
		if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
			handleFieldChange('tags', [...formData.tags, tagInput.trim()]);
			setTagInput('');
		}
	};

	// Handle tag removal
	const handleRemoveTag = (tagToRemove: string) => {
		handleFieldChange(
			'tags',
			formData.tags.filter((tag) => tag !== tagToRemove),
		);
	};

	// Handle form submission (for publish)
	const handleSubmit = async (e: React.FormEvent, publish: boolean = true) => {
		e.preventDefault();

		if (!formData.title.trim()) {
			setSaveStatus('error');
			return;
		}

		setSaveStatus('saving');

		try {
			if (isEditMode && id) {
				const updateData: UpdateBlogRequest = {
					blog_id: id,
					title: formData.title,
					content: formData.content,
					excerpt: formData.excerpt,
					category: formData.category,
					tags: formData.tags,
					is_published: publish,
					featured_image_url: formData.featured_image_url,
				};

				const result = await updateBlog(id, updateData);
				if (result && result.id) {
					setSaveStatus('saved');
					toast.success(publish ? 'Blog published successfully!' : 'Draft saved successfully!');
					// Update formData to reflect the published state
					handleFieldChange('is_published', publish);
					setTimeout(() => {
						navigate(ROUTES.BLOG_POST(result.slug || id));
					}, 1500);
				} else {
					setSaveStatus('error');
					toast.error('Failed to update blog post');
				}
			} else {
				const createData: CreateBlogRequest = {
					title: formData.title,
					content: formData.content,
					excerpt: formData.excerpt,
					category: formData.category,
					tags: formData.tags,
					is_published: publish,
					featured_image_url: formData.featured_image_url,
				};

				const result = await createBlog(createData);
				if (result && result.id) {
					setSaveStatus('saved');
					toast.success(publish ? 'Blog published successfully!' : 'Draft saved successfully!');
					// Update formData to reflect the published state
					handleFieldChange('is_published', publish);
					setTimeout(() => {
						if (publish) {
							navigate(ROUTES.BLOG_POST(result.slug || result.id));
						} else {
							navigate(ROUTES.DRAFTS);
						}
					}, 1500);
				} else {
					setSaveStatus('error');
					toast.error('Failed to create blog post');
				}
			}
		} catch (error) {
			logger.error('Error saving blog', 'BlogEditorPage', { error });
			setSaveStatus('error');
			const errorMessage = error instanceof Error ? error.message : 'Failed to save blog post';
			toast.error(errorMessage);
		}
	};

	// Handle save as draft
	const handleSaveAsDraft = async () => {
		const draftEvent = { preventDefault: () => {} } as React.FormEvent;
		await handleSubmit(draftEvent, false);
	};

	// Handle publish
	const handlePublish = async () => {
		const publishEvent = { preventDefault: () => {} } as React.FormEvent;
		await handleSubmit(publishEvent, true);
	};

	// Loading state for edit mode
	if (isEditMode && (fetchLoading || !formData.title)) {
		return (
			<div className={`min-h-screen flex items-center justify-center ${theme.background}`}>
				<div className={`${theme.card} rounded-2xl p-8`}>
					<div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
					<p className={theme.textSecondary}>Loading blog post...</p>
				</div>
			</div>
		);
	}

	const loading = createLoading || updateLoading;
	const error = createError || updateError || fetchError;

	return (
		<div className={`min-h-screen ${theme.background}`}>
			{/* Header */}
			<header className={`${theme.header} sticky top-0 z-40`}>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='flex items-center justify-between h-16'>
						<Button
							variant='outline'
							onClick={() => {
								// If editing a draft, go back to drafts page, otherwise go to blog page
								if (isEditMode && !formData.is_published) {
									navigate(ROUTES.DRAFTS);
								} else {
									navigate(ROUTES.BLOG);
								}
							}}
							className="flex items-center gap-2">
							<svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
								<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
							</svg>
							{isEditMode && !formData.is_published ? 'Back to Drafts' : 'Back to Blog'}
						</Button>

						<div className='flex items-center gap-4'>
							{/* Status Display */}
							<div className='flex items-center gap-2'>
								<div
									className={`w-2 h-2 rounded-full ${
										saveStatus === 'saved'
											? theme.textSuccess
											: saveStatus === 'saving'
											? `${theme.textInfo} animate-pulse`
											: saveStatus === 'error'
											? theme.textError
											: theme.textMuted
									}`}></div>
								<span className={`text-sm ${theme.textSecondary}`}>
									{saveStatus === 'saved' && 'Saved'}
									{saveStatus === 'saving' && 'Saving...'}
									{saveStatus === 'error' && 'Error'}
									{saveStatus === 'idle' && 'Draft'}
								</span>
							</div>

							<Button
								type='button'
								variant='outline'
								onClick={handleSaveAsDraft}
								disabled={loading || !formData.title.trim()}
								className="hidden sm:flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
								<svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12'
									/>
								</svg>
								Save Draft
							</Button>

							<Button
								type='button'
								onClick={handlePublish}
								disabled={loading || !formData.title.trim()}
								className="flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
								loading={loading}>
								{!loading && (
									<svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
										<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 19l9 2-9-18-9 18 9-2zm0 0v-8' />
									</svg>
								)}
								{isEditMode ? 'Update & Publish' : 'Publish Story'}
							</Button>
						</div>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
				{/* Status Messages */}
				{error && (
					<div className='mb-6'>
						<Alert type='error' message={error} />
					</div>
				)}

				{saveStatus === 'saved' && (
					<div className='mb-6'>
						<Alert type='success' message={`Blog post ${isEditMode ? 'updated' : 'created'} successfully! Redirecting...`} />
					</div>
				)}

				{saveStatus === 'error' && (
					<div className='mb-6'>
						<Alert type='error' message={`Failed to ${isEditMode ? 'update' : 'create'} blog post. Please try again.`} />
					</div>
				)}

				<div className='grid grid-cols-1 lg:grid-cols-5 gap-8'>
					{/* Main Editor */}
					<div className='lg:col-span-4'>
						<div className='space-y-4'>
							{/* Title */}
							<div className={`${theme.card} rounded-lg p-4`}>
								<input
									id='title'
									type='text'
									value={formData.title}
									onChange={(e) => handleFieldChange('title', e.target.value)}
									placeholder='Give your story a compelling title...'
									required
									disabled={isEditMode && editRestrictions.title}
									className={`w-full text-xl sm:text-2xl font-bold ${theme.textPlaceholder} bg-transparent border-none outline-none ${theme.textPrimary} focus:ring-0`}
									style={{ lineHeight: '1.3' }}
								/>
							</div>

							{/* Content Editor */}
							<div className={`${theme.card} rounded-lg`}>
								<RichTextEditor
									content={formData.content}
									onChange={(content: string) => handleFieldChange('content', content)}
									placeholder='Start writing your story...'
									readOnly={isEditMode && editRestrictions.content}
								/>

								{isEditMode && editRestrictions.content && (
									<div className={`p-4 ${theme.alertWarning} border-t`}>
										<p className={`${theme.textInfo} text-sm`}>Content editing is restricted for this blog post.</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Settings Sidebar */}
					<div className='lg:col-span-1'>
							<div className={`${theme.card} rounded-lg p-6 space-y-6 sticky top-24`}>
							<h3 className={`text-lg font-semibold ${theme.textHeading} border-b ${theme.textSecondary} pb-3`}>Story Settings</h3>

							{/* Excerpt */}
							<div>
								<label htmlFor='excerpt' className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
									Excerpt
								</label>
								<textarea
									id='excerpt'
									key={`excerpt-${resolvedTheme}`} // Force re-render on theme change
									value={formData.excerpt}
									onChange={(e) => handleFieldChange('excerpt', e.target.value)}
									placeholder='Brief description for previews...'
									disabled={isEditMode && editRestrictions.excerpt}
									className={`w-full px-3 py-2 ${theme.input} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 ${theme.textAccent} text-sm resize-none ${theme.textPlaceholder}`}
									rows={3}
								/>
							</div>

							{/* Featured Image */}
							<div>
								<label htmlFor='featuredImage' className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
									Featured Image URL
								</label>
								<Input
									id='featuredImage'
									type='url'
									value={formData.featured_image_url}
									onChange={(e) => handleFieldChange('featured_image_url', e.target.value)}
									placeholder='https://example.com/image.jpg'
									disabled={isEditMode && editRestrictions.featured_image_url}
									className='w-full text-sm'
								/>
								{formData.featured_image_url && (
									<div className='mt-3'>
										<img
											src={formData.featured_image_url}
											alt='Featured preview'
											className='w-full h-32 object-cover rounded-lg'
											onError={(e) => {
												e.currentTarget.style.display = 'none';
											}}
										/>
									</div>
								)}
							</div>

							{/* Tags */}
							<div>
								<label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Tags</label>
								{!(isEditMode && editRestrictions.tags) && (
									<div className='flex gap-2 mb-3'>
										<Input
											type='text'
											value={tagInput}
											onChange={(e) => setTagInput(e.target.value)}
											placeholder='Add a tag...'
											onKeyPress={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
													handleAddTag();
												}
											}}
											className='flex-1 text-sm'
										/>
										<Button type='button' onClick={handleAddTag} size='sm'>
											Add
										</Button>
									</div>
								)}

								<div className='flex flex-wrap gap-2'>
									{formData.tags.map((tag) => (
										<span key={tag} className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${theme.badge} ${theme.textSecondary}`}>
											{tag}
											{!(isEditMode && editRestrictions.tags) && (
												<button
													type='button'
													onClick={() => handleRemoveTag(tag)}
													className={`ml-1 ${theme.textAccent} hover:${theme.textPrimary}`}>
													×
												</button>
											)}
										</span>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
};

export default BlogEditorPage;
